import { useMemo, useRef, useState } from "react";
import type { GriddSnapshot } from "@gridd/embed/react";
import {
  ArrowDown,
  ArrowUp,
  Columns2,
  CornerDownRight,
  Eraser,
  Heading1,
  IndentIncrease,
  Link2,
  Milestone,
  Paperclip,
  PackagePlus,
  Plus,
  RotateCcw,
  Rows2,
  Square,
  Trash2,
  type LucideIcon,
} from "lucide-react";

import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ContentWindow } from "@/components/deck/ContentWindow";
import { SplitResizer } from "@/components/deck/SplitResizer";
import { useElementWidth } from "@/hooks/useElementWidth";
import { useImageAsset } from "@/hooks/useImageAsset";
import { dropContentArtifacts } from "@/lib/contentCleanup";
import { logoBox, logoOffsets, logoReserve } from "@/lib/deck-logo";
import { deckOwner, nuggetOwner } from "@/lib/ownerKey";
import { levelOf } from "@/lib/pyramid";
import { SLIDE_ASPECT, SLIDE_H, SLIDE_W } from "@/lib/slide-size";
import {
  activePresetLabel,
  DEFAULT_SPLIT,
  RATIO_PRESETS,
} from "@/lib/slide-split";
import {
  ptToPx,
  resolveStickerTone,
  resolveStudioTypography,
  type ResolvedTypography,
} from "@/lib/stylesheet-typography";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import type { SlideContent } from "@/types/content";
import type {
  SlideContents,
  SlideCorners,
  SlideLayout,
  SlideLevel,
  StickerTone,
} from "@/types/document";

// SlideData is defined in the document model (its `contents` array holds the windows);
// re-exported here so existing `@/components/deck/Slide` imports keep working.
export type { SlideData } from "@/types/document";
import type { SlideData } from "@/types/document";

// Flatten a resolved stylesheet role into an inline style. The size is authored in points
// (§1.4); convert to CSS px here so it paints physically correct under the scale transform.
function typographyToStyle(t: ResolvedTypography): React.CSSProperties {
  return {
    fontFamily: t.fontFamily,
    fontSize: ptToPx(t.fontSizePt),
    color: t.color,
    fontWeight: t.fontWeight,
    fontStyle: t.fontStyle,
  };
}

/**
 * One chrome-row action, collapsed to its icon.
 *
 * The row carries five of these beside two toggle groups, and the labels were wide enough to
 * wrap it onto a second line in a narrow editor column — which matters more than it looks,
 * because §23.2.1 is about to ask for a tone picker in the same row and the row's height
 * pushes every slide below it down the column.
 *
 * The label is not dropped with the text: it is the tooltip *and* the accessible name, so the
 * control still says what it does on hover and still announces itself to a screen reader.
 * `w-8` against `size="sm"`'s `h-8` makes a square that lines up exactly with the `size="sm"`
 * toggle items beside it.
 *
 * A disabled button shows no tooltip — `disabled:pointer-events-none` means the trigger never
 * sees the hover. That is the standard behaviour and it is left alone: the two that can be
 * disabled are the move arrows, where "why is this greyed out" is answered by the arrow
 * pointing off the end of the deck.
 */
function IconAction({
  label,
  icon: Icon,
  onClick,
  disabled,
  className,
}: {
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("w-8 px-0", className)}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// §23.2.2 Four choices, one ToggleGroup, built like the split presets beside it. The swatches
// are indicative only — the pill itself paints from the SHEET's `danger`/`warning`/`success`
// (§23.1.2), so a deck with its own colour table shows its own reds. A picker that resolved
// the live sheet would be more faithful and would also make this control re-render on every
// sheet keystroke, for a 12px dot.
const STICKER_TONE_CHOICES: { value: string; label: string; swatch: string }[] =
  [
    { value: "default", label: "Default tone", swatch: "#e2e8f0" },
    { value: "red", label: "Red — blocked", swatch: "#DD0000" },
    { value: "yellow", label: "Yellow — at risk", swatch: "#FFD000" },
    { value: "green", label: "Green — on track", swatch: "#009917" },
  ];

// §18.2.7 The three levels, on the same kind of control as the layout switcher beside them —
// a level IS a choice about this slide, and it belongs in the chrome row for the reason §23.2
// gives about the sticker tone: anything that only appears inside the canvas while editing
// changes the content window's height, and the editor stops previewing what it exports.
const LEVEL_CHOICES: { value: SlideLevel; label: string; icon: LucideIcon }[] =
  [
    { value: "storyline", label: "Storyline — the argument", icon: Milestone },
    {
      value: "evidence",
      label: "Evidence — supports the slide above",
      icon: CornerDownRight,
    },
    { value: "backup", label: "Backup — behind the deck", icon: Paperclip },
  ];

/**
 * One of the cover's four corner fields (§19.0.2) — client, date, author, confidentiality.
 *
 * Rendered in BOTH modes with the same element, like the subtitle/sticker row (§7.2/§7.3 and
 * the B4 fix): at rest an empty corner simply has no text, no placeholder and no rule, so it
 * "vanishes" (§19.1.3) without the row losing its height. Removing the element instead would
 * let the title block — which is vertically centred between the two corner rows — sit at a
 * different height in presentation than in the editor, which is the same class of WYSIWYG
 * bug the sticker row already carries a long comment about.
 */
function CornerField({
  value,
  onChange,
  placeholder,
  readOnly,
  style,
  align,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  readOnly: boolean;
  style: React.CSSProperties;
  align: "left" | "right";
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={readOnly ? "" : placeholder}
      readOnly={readOnly}
      tabIndex={readOnly ? -1 : undefined}
      style={style}
      className={cn(
        // The dashed rule is always THERE and only ever changes colour: a border that
        // exists in the editor and not at rest would make the row 1px taller while
        // editing, and the title block between the two corner rows is centred against
        // exactly that height. Same reasoning as the sticker's zero-width-but-mounted
        // trick above — an editor that is 1px out of step with the export is the bug §7
        // and B4 keep re-teaching.
        "h-auto min-w-0 flex-1 rounded-none border-0 border-b border-dashed border-transparent bg-transparent px-0 py-1 focus-visible:ring-0",
        align === "right" && "text-right",
        !readOnly && "border-slate-200 focus-visible:border-slate-400",
      )}
    />
  );
}

export function Slide({
  slide,
  index,
  onChange,
  onDelete,
  onRestore,
  onPurge,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onInsertAfter,
  onAddEvidence,
  onSetLevel,
  onSaveAsNugget,
  onBreakLink,
  nuggetName,
  nuggetUpdatedAt,
  levelLocked = false,
  onSelect,
  pageNumber,
  presenting = false,
  locked = false,
  snapshots,
}: {
  slide: SlideData;
  index: number;
  onChange: (patch: Partial<SlideData>) => void;
  onDelete?: () => void;
  onRestore?: () => void;
  // §20 Permanent delete, offered ONLY on an archived slide — the archive is what makes
  // every other delete in the app safe, so this is the one action that leaves it.
  onPurge?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  // §18.2.2 Insert a new, empty slide directly below this one, at this slide's own level —
  // so "+" on an evidence slide adds a sibling rather than splitting the block. Undefined on
  // the archive and on read-only paths, where the whole chrome row is hidden anyway.
  onInsertAfter?: () => void;
  // §18.2.7 The other half of the same button: insert an EVIDENCE slide under this one.
  // Undefined where it makes no sense — a backup slide has nothing to prove, and a cover is
  // not part of the argument.
  onAddEvidence?: () => void;
  // §18.2.7 Set this slide's level in the pyramid (§18.1.1).
  onSetLevel?: (level: SlideLevel) => void;
  // §17.4.4 Save this slide to the nugget shelf and link it. Offered only on an unlinked,
  // editable slide — a linked slide is already a nugget, and saving it again would make a
  // second nugget out of a body the first one owns.
  onSaveAsNugget?: () => void;
  // §17.4.3 Break the link: this slide becomes this deck's own, for good. `thenClear` is
  // §17.6.3's break-then-clear, which the editor completes after the repoint lands.
  onBreakLink?: (opts?: { thenClear?: boolean }) => void;
  // §17.4.2 What the badge's menu says. The name is the nugget's — which is this slide's own
  // title (§17.4.6), so it is normally a restatement; it is shown anyway, because the menu is
  // where someone goes to ask "what IS this thing linked to", and answering with a blank is
  // not an answer. `undefined` on every slide that is not linked.
  nuggetName?: string;
  nuggetUpdatedAt?: string;
  // §18.2.1 This is the deck's first active slide, whose level the reducer refuses to change:
  // the first slide is the deck's first claim. Disabled rather than silently ignored, so the
  // refusal is visible before the click rather than after it.
  levelLocked?: boolean;
  // §8.0.2 "last interacted" signal: fired whenever focus lands anywhere inside this
  // slide (title/subtitle/sticker/source/content), marking it the current selection.
  // Only wired for active, editable slides.
  onSelect?: () => void;
  // §19.0.3/§18.2.9 The number to draw on the canvas and to label the chrome row with, from
  // `slideNumbering(active)` — `4`, `4.2`, `B7`, or `null` on a cover, which carries none.
  // Every deck path passes it; the fallback to `index + 1` is only for the archive, whose
  // slides draw no number at all.
  pageNumber?: string | null;
  presenting?: boolean;
  // §12.5.2 This page doesn't hold the deck's write lock, so the slide renders as a viewer:
  // no editing, no chrome. Distinct from `presenting`, which is the fullscreen overlay, and
  // from `archived`, which is a property of the slide rather than of the page.
  locked?: boolean;
  // Print path only (TODO §6.6.4): griddDocId→snapshot map, passed straight to each window
  // so its grid renders as a static image instead of a live iframe.
  snapshots?: Record<string, GriddSnapshot>;
}) {
  const dispatch = useAppDispatch();
  const readOnly = presenting || locked || slide.archived;
  // The editing chrome (layout switcher, split presets, Clear/Delete/Move) is meaningless
  // without write access, so it goes away entirely rather than sitting there disabled.
  const chromeHidden = presenting || locked;

  // Slide chrome resolves its typography against the deck stylesheet — the same source of
  // truth as grids and text blocks (TODO §7.4) — so a sheet swap restyles slide titles too.
  // Title/subtitle/source are SlideCraft's own presets, injected into the sheet and edited
  // in the stylesheet panel like any other (@/lib/studio-presets); the sticker takes the
  // sheet's highlight role. Nothing here is a hardcoded size or colour any more.
  const styleSheet = useAppSelector(selectEffectiveStyleSheet);
  const titleStyle = useMemo(
    () => typographyToStyle(resolveStudioTypography(styleSheet, "Title")),
    [styleSheet],
  );
  const subtitleStyle = useMemo(
    () => typographyToStyle(resolveStudioTypography(styleSheet, "Subtitle")),
    [styleSheet],
  );
  const sourceStyle = useMemo(
    () => typographyToStyle(resolveStudioTypography(styleSheet, "Source")),
    [styleSheet],
  );
  // §23.0.2 The sticker's own injected preset (Body + bold), not the sheet's Highlight —
  // which in Gridd's factory sheet is Calisto MT 22pt, larger than the slide title and in a
  // family nothing else on the slide uses, so it read as a second title. A status flag wants
  // the quietest typography on the slide and the loudest colour.
  const stickerStyle = useMemo(
    () => typographyToStyle(resolveStudioTypography(styleSheet, "Sticker")),
    [styleSheet],
  );
  // §23.1 The tone, resolved through the sheet's colour NAMES. No tone = the preset's own
  // colour on the neutral pill this always had, which is what every pre-§23 sticker gets.
  const toneColors = useMemo(
    () =>
      slide.stickerTone
        ? resolveStickerTone(styleSheet, slide.stickerTone)
        : null,
    [styleSheet, slide.stickerTone],
  );

  // §24.2.1 The deck's logo, as an overlay INSIDE the logical canvas. One insertion point
  // here gives the editor, presentation, print and the offscreen thumbnail capturer the
  // same mark in the same place, because all four render this component; only `htmlExport`
  // needs its own copy (§24.4), and it reads the geometry from the same helpers.
  // §24.2.3 Every layout shows it, the cover included, in the same corner.
  const deckId = useAppSelector((s) => s.document.id);
  // §17.1.3 Which owner key a body created on this slide is filed under. A linked slide's
  // bodies are the NUGGET's — that is the whole of what the link means — and this is the one
  // place in the component tree that can see both the slide and the deck.
  const contentOwner = slide.link
    ? nuggetOwner(slide.link.nuggetId)
    : deckOwner(deckId);
  const logo = useAppSelector((s) => s.document.logo);
  const logoUrl = useImageAsset(logo?.assetId);
  // §24.2.2 The one padding rule, computed from the shared helper so the on-screen canvas
  // and the exported one cannot drift. Stated by EDGE; each layout maps the edge to
  // whichever of its own rows sit on it.
  const reserve = useMemo(() => logoReserve(logo), [logo]);

  // §19.0.3 A cover draws no page number; everything else draws the one it was given.
  // The `layout` check is belt and braces on top of the helper: a caller that forgets to
  // pass `pageNumber` then still gets the cover right, and only the offset wrong.
  const pageNo =
    slide.layout === "cover" ? null : (pageNumber ?? String(index + 1));
  // §18.1.1 Resolved through the one helper, never as `slide.level ?? "storyline"`.
  const level = levelOf(slide);

  // The one-Gridd rule (§4.3): at most one window may hold a Gridd. Computed once and
  // passed to every window so each gates its own "Add Gridd" button.
  const hasGridd = slide.contents.some((c) => c?.kind === "gridd");

  // Fixed logical canvas (§1.4): the slide's content is authored at SLIDE_W×SLIDE_H and
  // the whole canvas is scaled to the measured outer width. Dragging the panels changes
  // that width, so the slide rescales without ever reflowing its contents.
  const [frameRef, frameWidth] = useElementWidth<HTMLDivElement>();
  const scale = frameWidth > 0 ? frameWidth / SLIDE_W : 0;

  // Two-window split ratio (§4.7): the first window's fraction. During a resize drag we
  // hold a live local ratio (smooth, no store writes) and commit once on release.
  const splitRef = useRef<HTMLDivElement>(null);
  const [dragRatio, setDragRatio] = useState<number | null>(null);
  const ratio = dragRatio ?? slide.splitRatio ?? DEFAULT_SPLIT;
  const commitRatio = (r: number) => {
    setDragRatio(null);
    if (r !== (slide.splitRatio ?? DEFAULT_SPLIT)) onChange({ splitRatio: r });
  };

  // Replace one window's content, keeping the other window untouched. Flows out via
  // onChange → updateSlide → putDeck, same path as every other slide edit.
  const setWindowContent = (windowIndex: number, content: SlideContent) => {
    const next = [...slide.contents] as SlideContents;
    next[windowIndex] = content;
    onChange({ contents: next });
  };

  // §19.0.2 One corner at a time, merged into the single `corners` object.
  const setCorner = (key: keyof SlideCorners, value: string) => {
    onChange({ corners: { ...(slide.corners ?? {}), [key]: value } });
  };

  // Layout switcher (§4.4). single ↔ two-* changes the window *count*:
  //   single → two-*  : add an empty second window.
  //   two-*  → single : keep the first window, drop the second — warning first if it
  //                     actually holds content, and cleaning up its grid/image so
  //                     nothing orphans (the orphan rule again).
  //   two-h  ↔ two-v  : same two windows, only the arrangement changes.
  const changeLayout = (next: SlideLayout) => {
    if (next === slide.layout) return;
    // §19.0.5 A cover keeps whatever `contents` it has and simply does not draw them, so
    // switching TO one touches nothing else. Clearing the windows here would run
    // `dropContentArtifacts` and destroy a grid body for what is only a layout change —
    // and switching back would then find the slide empty. It must NOT fall through to the
    // two-window branch below either, which would materialise a second empty window.
    if (next === "cover") {
      onChange({ layout: "cover" });
      return;
    }
    if (next === "single") {
      const second = slide.contents[1] ?? null;
      if (second) {
        if (
          !window.confirm(
            "Switch to a single window? The second window's content will be removed.",
          )
        )
          return;
        dropContentArtifacts(dispatch, second, deckId);
      }
      onChange({ layout: "single", contents: [slide.contents[0] ?? null] });
    } else {
      onChange({
        layout: next,
        contents: [slide.contents[0] ?? null, slide.contents[1] ?? null],
      });
    }
  };

  // Clear Slide empties every window (dropping each grid body / image asset so nothing
  // orphans in IndexedDB — TODO §4.6) and clears title/source, but keeps the chosen
  // layout: clearing content shouldn't silently collapse a two-window slide.
  //
  // §17.6.3 **On a linked slide it breaks the link first, and it asks.** Emptying a window is
  // an edit to the nugget by definition — the ordinary path would project four empty windows
  // into every other deck that uses the slide, which is almost never what "clear this one
  // slide" meant. The honest sequence is break-then-clear, so the dialog offers exactly that
  // and cancelling leaves the slide untouched rather than half-done.
  //
  // The gate in `dropContentArtifacts` makes the order safe either way: it unlinks a shared
  // body and never deletes it. Breaking first is what makes the CLEAR correct, not what makes
  // the cleanup safe.
  const clearSlide = () => {
    if (slide.link) {
      if (
        !window.confirm(
          "This slide is a knowledge nugget shared with other decks. Clearing it here would empty it in all of them.\n\nBreak the link first, then clear this deck's own copy?",
        )
      ) {
        return;
      }
      // The break AND the clear both belong to the editor from here: the clear has to run
      // against the contents the break just wrote, and this component holds `slide` as a
      // prop — a value that is still the pre-break one inside this closure. `thenClear` also
      // suppresses the editor's own break confirm, since the question above already asked it.
      onBreakLink?.({ thenClear: true });
      return;
    }
    slide.contents.forEach((c) =>
      dropContentArtifacts(dispatch, c ?? null, deckId),
    );
    const cleared = slide.contents.map(() => null) as SlideContents;
    onChange({ title: "", source: "", contents: cleared });
  };

  return (
    <div
      id={`slide-${slide.id}`}
      // Capture focus of any descendant input/content so interacting with the slide marks
      // it selected (§8.0.2). No-op for archived/presenting slides (no onSelect passed).
      onFocusCapture={onSelect}
      className="w-full scroll-mt-20"
      // Capped at the logical width in the editor column; uncapped when presenting so the
      // fullscreen canvas can scale *up* to fill a large viewport (§5.1). The parent box
      // sets the actual width in that mode.
      style={{ maxWidth: presenting ? undefined : SLIDE_W }}
    >
      {!chromeHidden && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {/* §18.2.9 The row's label is the same hierarchical number the canvas draws, so
              the chrome and the exported page never disagree about what this slide is
              called. A cover has no number and says so. */}
          <span className="mr-1 text-xs font-medium text-slate-500">
            {slide.archived
              ? `Archived #${index + 1}`
              : pageNo === null
                ? "Cover"
                : `Slide ${pageNo}`}
          </span>
          <TooltipProvider delayDuration={400}>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {!slide.archived && (
                <>
                  {/* Per-slide layout: one window, or two arranged horizontally/vertically
                      (§4.4). */}
                  <ToggleGroup
                    type="single"
                    value={slide.layout}
                    onValueChange={(v) => v && changeLayout(v as SlideLayout)}
                    variant="outline"
                    size="sm"
                    aria-label="Slide layout"
                  >
                    <ToggleGroupItem
                      value="single"
                      aria-label="Single window"
                      title="Single window"
                    >
                      <Square className="h-4 w-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="two-h"
                      aria-label="Two windows side by side"
                      title="Two windows side by side"
                    >
                      <Columns2 className="h-4 w-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="two-v"
                      aria-label="Two windows stacked"
                      title="Two windows stacked"
                    >
                      <Rows2 className="h-4 w-4" />
                    </ToggleGroupItem>
                    {/* §19.1.1 The cover is a fourth value on the control that already
                        makes exactly this choice — not a separate button elsewhere. */}
                    <ToggleGroupItem
                      value="cover"
                      aria-label="Cover page"
                      title="Cover page"
                    >
                      <Heading1 className="h-4 w-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>
                  {/* §18.2.7 The slide's level in the pyramid, beside the layout toggle.
                      Not offered on a cover: a title page is not a claim, so it has no place
                      in the argument and giving it evidence would number that evidence under
                      a slide that draws no number. On the deck's FIRST slide the other two
                      are disabled rather than hidden — the control still shows what the slide
                      is, and §18.2.1's refusal is legible before the click. */}
                  {onSetLevel && slide.layout !== "cover" && (
                    <ToggleGroup
                      type="single"
                      value={level}
                      onValueChange={(v) => v && onSetLevel(v as SlideLevel)}
                      variant="outline"
                      size="sm"
                      aria-label="Pyramid level"
                    >
                      {LEVEL_CHOICES.map((c) => (
                        <ToggleGroupItem
                          key={c.value}
                          value={c.value}
                          aria-label={c.label}
                          title={
                            levelLocked && c.value !== "storyline"
                              ? "The deck's first slide is its first claim — it stays on the storyline"
                              : c.label
                          }
                          disabled={levelLocked && c.value !== "storyline"}
                        >
                          <c.icon className="h-4 w-4" />
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  )}
                  {/* Exact split presets for the two-* layouts — pick once, reuse across
                      the deck for pixel-perfect consistency (§4.7). The drag divider on the
                      slide still fine-tunes between them. Label = first window's fraction. */}
                  {slide.layout !== "single" && slide.layout !== "cover" && (
                    <ToggleGroup
                      type="single"
                      value={activePresetLabel(ratio) ?? ""}
                      onValueChange={(v) => {
                        const preset = RATIO_PRESETS.find((p) => p.label === v);
                        if (preset) commitRatio(preset.value);
                      }}
                      variant="outline"
                      size="sm"
                      aria-label="Content split ratio"
                    >
                      {RATIO_PRESETS.map((p) => (
                        <ToggleGroupItem
                          key={p.label}
                          value={p.label}
                          title={`First window ${p.label}`}
                          className="px-2 text-xs tabular-nums"
                        >
                          {p.label}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  )}
                  {/* §23.2 The sticker's tone. It lives HERE, in the chrome bar, and not
                      as a caret inside the pill — B4 part 3 restated: any control that
                      appears inside the logical canvas only while editing changes the
                      subtitle row's height, which changes the content window's height, which
                      means the editor stops previewing what it exports and an image is fitted
                      to a box the author never saw. This row is already outside the canvas
                      and already hidden when `chromeHidden`, so it costs no geometry at all.
                      Offered only once there is a sticker to tone. "Default" is a real
                      choice, not the absence of one: it clears the field to `undefined`. */}
                  {slide.sticker && (
                    <ToggleGroup
                      type="single"
                      value={slide.stickerTone ?? "default"}
                      onValueChange={(v) =>
                        v &&
                        onChange({
                          stickerTone:
                            v === "default" ? undefined : (v as StickerTone),
                        })
                      }
                      variant="outline"
                      size="sm"
                      aria-label="Sticker tone"
                    >
                      {STICKER_TONE_CHOICES.map((t) => (
                        <ToggleGroupItem
                          key={t.value}
                          value={t.value}
                          aria-label={t.label}
                          title={t.label}
                          className="px-2"
                        >
                          <span
                            className="h-3 w-3 rounded-full border border-slate-300"
                            style={{ backgroundColor: t.swatch }}
                          />
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  )}
                  {/* === §17.4.1/§17.4.2 The nugget badge === */}
                  {/* It sits in the slide CHROME, never on the canvas. Anything inside
                      `.slide-canvas` is exported: a link glyph there would land in the PDF,
                      in the HTML export and in presentation mode, on every deck that uses
                      the slide. This row is already outside the canvas and already hidden
                      whenever the slide is being *shown* rather than edited.

                      It is a MENU rather than a bare icon, because the two questions someone
                      has when they notice it — "linked to what?" and "how do I stop?" — both
                      need somewhere to be answered. */}
                  {slide.link && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-8 px-0 text-sky-700"
                          aria-label="Knowledge nugget"
                          title="This slide is a knowledge nugget"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72">
                        <DropdownMenuLabel className="truncate">
                          {nuggetName?.trim() || "Untitled nugget"}
                        </DropdownMenuLabel>
                        <div className="px-2 pb-2 text-xs text-slate-500">
                          {/* §17.0 The sentence the whole feature turns on, said where
                              somebody will actually read it: one deck is open per page, so
                              "edited everywhere" means the edit lands on the nugget now and
                              every other deck shows it the next time it is opened. */}
                          Edits here reach every deck that uses this slide.
                          {nuggetUpdatedAt && (
                            <>
                              {" "}
                              Last updated{" "}
                              {new Date(nuggetUpdatedAt).toLocaleString()}.
                            </>
                          )}
                        </div>
                        {onBreakLink && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => onBreakLink()}>
                              Break link
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* §17.4.4 …and the way in. One button, beside the badge it turns into. */}
                  {onSaveAsNugget && !slide.link && (
                    <IconAction
                      label="Save slide as nugget"
                      icon={PackagePlus}
                      onClick={onSaveAsNugget}
                    />
                  )}
                  {/* §18.2.2 The deck's only insert-at-position control: one button per
                      slide, adding an empty slide immediately below it. The append-to-the-end
                      button still lives at the foot of the column. */}
                  {onInsertAfter && (
                    <IconAction
                      label="Add slide below"
                      icon={Plus}
                      onClick={onInsertAfter}
                    />
                  )}
                  {/* §18.2.7 …and the same button one level down. Two buttons rather than a
                      menu because "add an evidence slide under this one" is the action this
                      whole feature exists to make possible, and burying it costs it. */}
                  {onAddEvidence && (
                    <IconAction
                      label="Add evidence below"
                      icon={IndentIncrease}
                      onClick={onAddEvidence}
                    />
                  )}
                  <IconAction
                    label="Clear slide"
                    icon={Eraser}
                    onClick={clearSlide}
                  />
                  {/* The one destructive action in the row, and the only one whose icon has
                      to carry that on its own now the word "Delete" is gone. Same red as the
                      Delete item in the documents list. */}
                  <IconAction
                    label="Delete slide"
                    icon={Trash2}
                    onClick={onDelete}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  />
                  {(onMoveUp || onMoveDown) && (
                    <>
                      <IconAction
                        label="Move slide up"
                        icon={ArrowUp}
                        onClick={onMoveUp}
                        disabled={!canMoveUp}
                      />
                      <IconAction
                        label="Move slide down"
                        icon={ArrowDown}
                        onClick={onMoveDown}
                        disabled={!canMoveDown}
                      />
                    </>
                  )}
                </>
              )}
              {/* The archive's own actions keep their labels. Restore, because it is the
                  rarer, less guessable verb; and §20's permanent delete because it is the
                  single action in the app that cannot be undone — a bare trash icon sitting
                  next to a bare restore icon is exactly the pair you do not want to tell
                  apart at a glance. Its red is the one the documents list already uses. */}
              {slide.archived && (
                <>
                  <Button variant="outline" size="sm" onClick={onRestore}>
                    <RotateCcw className="mr-2 h-4 w-4" /> Restore Slide
                  </Button>
                  {onPurge && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onPurge}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete permanently
                    </Button>
                  )}
                </>
              )}
            </div>
          </TooltipProvider>
        </div>
      )}

      {/* Outer reserves the scaled height (width × 16:9) so surrounding layout flows
          correctly; the inner logical canvas is scaled into it from the top-left. */}
      <div
        ref={frameRef}
        className="relative w-full overflow-hidden rounded-md bg-white shadow-xl ring-1 ring-slate-200"
        style={{ height: frameWidth * SLIDE_ASPECT }}
      >
        <div
          className="slide-canvas absolute left-0 top-0 flex flex-col gap-3 bg-white p-6"
          style={{
            width: SLIDE_W,
            height: SLIDE_H,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {/* §24.2.1 The mark. An `<img>` inside the captured canvas on purpose: the
              offscreen thumbnail capturer already waits on every `<img>` in the node before
              it rasterizes, so the logo is in that wait for free and cannot miss the frame
              (§24.2.4 — a logo-less capture would then stick, being cached on the deckIndex
              row). `alt=""` + aria-hidden: it is decoration repeated on every slide, and a
              screen reader announcing the company name forty times is noise. */}
          {logo && logoUrl && (
            <img
              src={logoUrl}
              alt=""
              aria-hidden
              className="pointer-events-none absolute select-none object-contain"
              style={{ ...logoOffsets(logo.corner), ...logoBox(logo.sizePct) }}
            />
          )}

          {slide.layout === "cover" ? (
            /* §19 The cover: four corner fields around a vertically centred title block,
               and no content region at all. The windows are NOT rendered and NOT cleared —
               `slide.contents` is carried untouched so cover → two-h → cover is lossless
               (§19.0.5). Every field resolves through the same stylesheet presets as the
               rest of the deck (§19.0.4), so a sheet swap restyles a cover too. */
            <>
              {/* §24.2.2 The corner row at the mark's edge yields to it, exactly as the
                  title row does on a content slide. On a cover there is one row per edge,
                  so this is the "padding on one row" the design asked for — and it is what
                  keeps a right-aligned corner field from running under a `tr`/`br` mark. */}
              <div
                className="flex items-start gap-6"
                style={{
                  paddingLeft: reserve.topLeft,
                  paddingRight: reserve.topRight,
                }}
              >
                <CornerField
                  value={slide.corners?.tl ?? ""}
                  onChange={(v) => setCorner("tl", v)}
                  placeholder="Top left"
                  readOnly={readOnly}
                  style={sourceStyle}
                  align="left"
                />
                <CornerField
                  value={slide.corners?.tr ?? ""}
                  onChange={(v) => setCorner("tr", v)}
                  placeholder="Top right"
                  readOnly={readOnly}
                  style={sourceStyle}
                  align="right"
                />
              </div>
              <div className="flex min-h-0 flex-1 flex-col justify-center gap-4">
                <AutoGrowTextarea
                  value={slide.title}
                  onChange={(e) => onChange({ title: e.target.value })}
                  placeholder={readOnly ? "" : "Deck title"}
                  readOnly={readOnly}
                  style={titleStyle}
                  className={cn(
                    "rounded-none border-0 border-b border-dashed border-transparent px-0 py-1 focus-visible:ring-0",
                    !readOnly && "focus-visible:border-slate-400",
                  )}
                />
                <AutoGrowTextarea
                  value={slide.subtitle ?? ""}
                  onChange={(e) => onChange({ subtitle: e.target.value })}
                  placeholder={readOnly ? "" : "Subtitle"}
                  readOnly={readOnly}
                  style={subtitleStyle}
                  className="rounded-none border-0 px-0 py-0 focus-visible:ring-0"
                />
              </div>
              <div
                className="flex items-end gap-6"
                style={{
                  paddingLeft: reserve.bottomLeft,
                  paddingRight: reserve.bottomRight,
                }}
              >
                <CornerField
                  value={slide.corners?.bl ?? ""}
                  onChange={(v) => setCorner("bl", v)}
                  placeholder="Bottom left"
                  readOnly={readOnly}
                  style={sourceStyle}
                  align="left"
                />
                <CornerField
                  value={slide.corners?.br ?? ""}
                  onChange={(v) => setCorner("br", v)}
                  placeholder="Bottom right"
                  readOnly={readOnly}
                  style={sourceStyle}
                  align="right"
                />
              </div>
            </>
          ) : (
            <>
              {/* Title in the stylesheet's Title preset (§7.4) — no longer hardcoded Tailwind,
              so editing that preset restyles every slide title. Auto-grows and
              wraps a long title onto extra lines; the flex-1 content region below gives up
              the space so the fixed canvas never changes size. */}
              <AutoGrowTextarea
                value={slide.title}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="Slide title"
                readOnly={readOnly}
                // §24.2.2 The row yields to the mark on the side it sits; zero when there is
                // no logo, or when it is in a bottom corner.
                style={{
                  ...titleStyle,
                  paddingLeft: reserve.topLeft,
                  paddingRight: reserve.topRight,
                }}
                className="border-0 border-b border-dashed border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-slate-400 py-1"
              />

              {/* Subtitle (Subtitle preset) + sticker (highlight role) — TODO §7.2/§7.3.
              **Always rendered, in both modes, with the same two controls** (B4 fix,
              part 3). It used to be `{(!readOnly || subtitle || sticker) && …}` with a
              different element in each mode, so a slide with neither set had a TALLER
              content window in presentation/print than in the editor — an image's fit was
              then computed against a different box, and the editor stopped previewing what
              it exported. An empty field at rest is made invisible; it is never removed.
              The general rule: any chrome made conditional on `readOnly` changes the
              content window's logical geometry, and every one of those is a WYSIWYG bug.
              `pointer-events-none` at rest keeps the readOnly controls from taking focus
              in presentation, where they would swallow the arrow keys. */}
              <div
                className={cn(
                  "flex items-start gap-3",
                  readOnly && "pointer-events-none",
                )}
                style={{
                  paddingLeft: reserve.topLeft,
                  paddingRight: reserve.topRight,
                }}
              >
                <AutoGrowTextarea
                  value={slide.subtitle ?? ""}
                  onChange={(e) => onChange({ subtitle: e.target.value })}
                  placeholder={readOnly ? "" : "Subtitle"}
                  readOnly={readOnly}
                  tabIndex={readOnly ? -1 : undefined}
                  style={subtitleStyle}
                  containerClassName="min-w-0 flex-1"
                  className="border-0 rounded-none px-0 focus-visible:ring-0 py-0"
                />
                {/* Set, or editable: a 160px badge. Empty at rest it takes no room at all —
                it used to stay 160px wide and invisible, which left a hole the subtitle
                wrapped around. The element itself stays MOUNTED (zero width, same py) so
                it still holds the row's line box: the row height, and therefore the
                content window below it, is identical in both modes (the B4 invariant
                above). `-ml-3` swallows the row's own gap-3 as well. */}
                <Input
                  value={slide.sticker ?? ""}
                  onChange={(e) => onChange({ sticker: e.target.value })}
                  placeholder={readOnly ? "" : "Sticker"}
                  readOnly={readOnly}
                  tabIndex={readOnly ? -1 : undefined}
                  // §23.0.4 Caps are OURS, not the sheet's: a Gridd preset carries family,
                  // weight, style, size and colour and has no text-transform, and inventing
                  // a key in someone else's format is exactly the bleed §23 exists to avoid.
                  // The stored `slide.sticker` keeps what was typed, so the value stays
                  // correct for search, export and anything that reads it later.
                  style={{
                    ...stickerStyle,
                    textTransform: "uppercase",
                    ...(toneColors && slide.sticker
                      ? {
                          backgroundColor: toneColors.background,
                          color: toneColors.color,
                          borderColor: toneColors.background,
                        }
                      : {}),
                  }}
                  className={cn(
                    "h-auto shrink-0 rounded-full py-0.5 text-center focus-visible:ring-0",
                    // §23.1.4 Empty at rest is still ZERO footprint — no width at all, the
                    // negative margin swallowing the row's gap — while the element stays
                    // MOUNTED so the row's height, and the content window below it, is
                    // identical in both modes. The redesign must not undo that invariant.
                    readOnly
                      ? slide.sticker
                        ? "w-40 border border-transparent bg-slate-100"
                        : "-ml-3 w-0 overflow-hidden border-0 bg-transparent px-0"
                      : "w-40 border border-dashed border-slate-200 bg-slate-50 focus-visible:border-slate-400",
                  )}
                />
              </div>

              {/* Content area (§4.2/§4.7). Always the SAME flex container with window "w0"
              as its first child, so switching layouts never changes w0's element type or
              position — it stays mounted (a Gridd embed or a loaded image in the first
              window survives single↔two and two-h↔two-v; §4.5). Remounting w0 was what
              mis-measured/mis-placed its image on the first two-column frame. `single`
              renders just w0; the two-* layouts append the resizer + w1. flexGrow splits
              by ratio (each window's `flex-1` gives flex-basis: 0, so grow is the whole
              story). */}
              <div
                ref={splitRef}
                className={cn(
                  "flex min-h-0 flex-1",
                  slide.layout === "two-v" ? "flex-col" : "flex-row",
                )}
              >
                <ContentWindow
                  key="w0"
                  content={slide.contents[0] ?? null}
                  onChange={(c) => setWindowContent(0, c)}
                  readOnly={readOnly}
                  hasGridd={hasGridd}
                  owner={contentOwner}
                  shared={slide.link !== undefined}
                  snapshots={snapshots}
                  style={
                    slide.layout === "single" ? undefined : { flexGrow: ratio }
                  }
                />
                {slide.layout !== "single" && (
                  <>
                    <SplitResizer
                      orientation={slide.layout === "two-h" ? "h" : "v"}
                      containerRef={splitRef}
                      disabled={readOnly}
                      onPreview={setDragRatio}
                      onCommit={commitRatio}
                    />
                    <ContentWindow
                      key="w1"
                      content={slide.contents[1] ?? null}
                      onChange={(c) => setWindowContent(1, c)}
                      readOnly={readOnly}
                      hasGridd={hasGridd}
                      owner={contentOwner}
                      shared={slide.link !== undefined}
                      snapshots={snapshots}
                      style={{ flexGrow: 1 - ratio }}
                    />
                  </>
                )}
              </div>

              {/* Source and the automatic page number (§7.1) share the deck's bottom rule.
              The source input flexes to leave room for the number, which sits in-line at
              the right — its margin matching the slide body's own padding (this row is
              inside the p-6 canvas, so its right edge lines up with the content above).
              §18.2.9 The number is the slide's place in the PYRAMID — `4`, `4.2`, `B7` —
              handed down from `slideNumbering(active)`, never derived from the `index` prop.
              Non-editable, not stored, and rendered inside the logical canvas so it scales
              identically in edit, present, print and the exported HTML: this span is
              export-path output, not chrome, which is what §18.2.9 means by the numbering
              helper needing the same care the traversal gets.
              Archived slides are excluded from the deck and show none. */}
              <div
                className="flex items-center gap-3 border-t border-dashed border-slate-200"
                style={{
                  paddingLeft: reserve.bottomLeft,
                  paddingRight: reserve.bottomRight,
                }}
              >
                <Input
                  value={slide.source}
                  onChange={(e) => onChange({ source: e.target.value })}
                  placeholder="Source"
                  readOnly={readOnly}
                  style={sourceStyle}
                  className="h-auto min-w-0 flex-1 rounded-none border-0 px-0 py-1 focus-visible:ring-0"
                />
                {!slide.archived && pageNo !== null && (
                  <span
                    className="shrink-0 select-none tabular-nums text-slate-400"
                    style={{ fontSize: 20 }}
                  >
                    {pageNo}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
